// communicatie — wat de afdrukeditor boven op het document legt: vellen papier, herhaalblokken en het
// tabelmenu. Zodat hij werkt als een tekstverwerker en niet als een bouwdoos.
//
// WAAROM. De documentweergave was een lange witte strook: de voettekst plakte direct onder de laatste
// zin en nergens was te zien waar een pagina ophield. Een eerdere poging zette knoppen op elk blok;
// dat werkt niet zoals mensen een brief bewerken. Nu: A4-vellen op een grijze ondergrond, zo breed als
// het papier met de marges van document.css (`@page { size: A4; margin: 2cm }`), tekst die niet meer
// op een vel past schuift door naar het volgende, en de voettekst staat onderaan elk vel. Typen,
// selecteren, knippen, plakken en slepen doet de browser zelf, net als in Word.
//
// Blokknoppen blijven alleen waar ze iets betekenen: bij een herhaalblok (`data-repeat` — één per dier,
// één per factuurregel). En in een tabel geeft de rechtermuisknop rijen en kolommen toevoegen en
// verwijderen.
//
// HOE, EN WAAROM ZO. Alles hier leeft BUITEN de <body> van het iframe: de lagen hangen aan het
// <html>-element, het doorschuiven naar een volgend vel gaat via CSS-regels in een eigen <style> in de
// <head>, en maten via CSS-variabelen. Opgeslagen wordt alleen `body.innerHTML` — dus een vel, een knop
// of een verschuiving kán niet in een sjabloon of PDF belanden. De enige wijzigingen in de body zijn de
// bewuste: typen, een herhaalblok verplaatsen, een rij of kolom erbij of eraf.
//
// Scripts draaien niet in het iframe (de CSP in het document weert ze); dit wordt allemaal van
// buitenaf aan het document gehangen, net als het onthouden van de cursor in RawPrintCanvas.

/** Een A4-vel en de marge die document.css eromheen houdt. */
const VEL_HOOGTE = "297mm";
const MARGE = "20mm";
/** Hoe ver de voettekst onder het inhoudsvlak uitsteekt (`.db-footer { bottom: -0.3cm }`). */
const VOET_ONDER = "3mm";
/**
 * Tot deze hoogte past een voettekst in de gewone ondermarge. Een hogere (een logo met een blok
 * stichtingsgegevens) krijgt zijn eigen ruimte: de tekst houdt dan boven de voettekst op in plaats van
 * eronder door te lopen. Dezelfde regel als de PDF-generator (PdfRenderService.VOET_RUIM_MM), zodat
 * de editor laat zien wat er gedrukt wordt.
 */
const VOET_RUIM_MM = 12;
const VOET_BODEM_MM = 17;
const VOET_LUCHT_MM = 3;
/**
 * Waar de bovenkant van een koptekst ligt en hoeveel lucht er tussen koptekst en tekst zit. Dezelfde regel als
 * de PDF-generator (PdfRenderService.KOP_BOVEN_MM / KOP_LUCHT_MM): de bovenmarge is 2 cm, of genoeg voor de
 * koptekst.
 */
const KOP_BOVEN_MM = 10;
const KOP_LUCHT_MM = 3;
/** De grijze ruimte tussen twee vellen, in pixels. */
export const TUSSEN_VELLEN = 16;

export interface Lagen {
  /** Opnieuw uitmeten: na laden, na typen, na een wijziging, na een venstermaat. */
  herbereken: () => void;
  /**
   * Een bewerking van buitenaf (een kleur, een inspringing): onthoudt wat er stond, zodat Ctrl+Z en
   * "ongedaan maken" hem terugdraaien, en geeft de nieuwe inhoud door.
   */
  bewerk: (werk: () => void, melding?: string) => void;
  /** Alles weer loskoppelen. */
  weg: () => void;
}

/** Een maat in CSS-eenheden omgerekend naar pixels in dít document. */
function px(d: Document, maat: string): number {
  const p = d.createElement("div");
  p.style.cssText = `position:absolute;visibility:hidden;height:${maat};width:1px;top:0;left:0`;
  d.documentElement.appendChild(p);
  const h = p.getBoundingClientRect().height;
  p.remove();
  return h;
}

const isVoet = (e: Element | null) => !!e && e.classList.contains("db-footer");
const isKop = (e: Element | null) => !!e && e.classList.contains("db-header");
/** Kop- en voettekst horen bij het vel, niet bij de tekst: ze schuiven niet door en krijgen geen blokknoppen. */
const isKopOfVoet = (e: Element | null) => isKop(e) || isVoet(e);

/** Welk blok: de koptekst voor elke pagina, een eigen koptekst voor pagina 1, of de voettekst. */
export type KopVoetSoort = "kop" | "kopEerste" | "voet";

const KOPVOET_KLASSE: Record<KopVoetSoort, string> = {
  kop: "db-header",
  kopEerste: "db-header db-header-eerste",
  voet: "db-footer",
};
const KOPVOET_KIEZER: Record<KopVoetSoort, string> = {
  kop: ".db-header:not(.db-header-eerste)",
  kopEerste: ".db-header-eerste",
  voet: ".db-footer",
};

/** Het kop- of voettekstblok van deze soort in het document (niet een kopie op een vel), of null. */
export function kopVoetVan(body: HTMLElement, soort: KopVoetSoort): HTMLElement | null {
  return Array.from(body.querySelectorAll<HTMLElement>(KOPVOET_KIEZER[soort]))
    .find((e) => !e.closest("#ce-vellen, #ce-lagen")) ?? null;
}

/**
 * Zet een kop- of voettekstblok in het document, of geeft het bestaande terug. Vooraan in het document: de
 * PDF-generator haalt het daar weg naar de paginamarge, en de editor zet het op het vel. Leeg, met één regel om
 * in te typen; een tekst als "Koptekst" zou anders zo op papier komen.
 */
export function voegKopVoetToe(body: HTMLElement, soort: KopVoetSoort): HTMLElement {
  const bestaand = kopVoetVan(body, soort);
  if (bestaand) return bestaand;
  const d = body.ownerDocument;
  const blok = d.createElement("div");
  blok.className = KOPVOET_KLASSE[soort];
  const regel = d.createElement("p");
  regel.appendChild(d.createElement("br"));
  blok.appendChild(regel);
  const houder = body.querySelector<HTMLElement>(".db-doc") ?? body;
  houder.insertBefore(blok, houder.firstChild);
  return blok;
}

/** Haalt een kop- of voettekstblok uit het document. */
export function verwijderKopVoet(body: HTMLElement, soort: KopVoetSoort): void {
  kopVoetVan(body, soort)?.remove();
}

/** Na dit element begint de tekst op een nieuw vel: een handmatig pagina-einde. */
function isPaginaEinde(e: Element): boolean {
  return e.classList.contains("db-pagebreak") || e.classList.contains("tt-page-break")
    || /page-break-after\s*:\s*always/i.test(e.getAttribute("style") ?? "");
}

/** Een CSS-kiezer die dit element aanwijst zonder er iets aan te veranderen. */
function kiezer(e: Element, body: HTMLElement): string {
  const delen: string[] = [];
  let x: Element | null = e;
  while (x && x !== body) {
    const ouder: HTMLElement | null = x.parentElement;
    if (!ouder) break;
    delen.unshift(`:nth-child(${Array.from(ouder.children).indexOf(x) + 1})`);
    x = ouder;
  }
  return `body > ${delen.join(" > ")}`;
}

/**
 * De stukken tekst die als geheel naar het volgende vel mogen: alinea's, tabellen, kopbalken.
 *
 * <p>Een herhaalblok of een voorwaarde (`<span data-if>`) om een paar blokken heen is geen stuk maar
 * een doos: daar kijken we in. En een blok dat hoger is dan een heel vel ook — anders schoof het
 * eindeloos door.
 */
function stroom(ouder: Element, vlak: number, win: Window, uit: HTMLElement[]): void {
  for (const k of Array.from(ouder.children) as HTMLElement[]) {
    if (isKopOfVoet(k)) continue;
    const s = win.getComputedStyle(k);
    if (s.display === "none" || s.position === "absolute" || s.position === "fixed") continue;
    if (s.display === "inline" || s.display === "contents") {
      if (Array.from(k.children).some((c) => win.getComputedStyle(c).display !== "inline")) {
        stroom(k, vlak, win, uit);
      }
      continue;
    }
    if (k.getBoundingClientRect().height > vlak && k.tagName !== "TABLE" && k.children.length) {
      stroom(k, vlak, win, uit);
      continue;
    }
    uit.push(k);
  }
}

// ── Tabellen ────────────────────────────────────────────────────────────────────────────────────

/** Waar een cel begint en hoe breed hij is, in kolommen (colspan telt mee). */
function kolomVan(cel: HTMLTableCellElement): { start: number; breed: number } {
  let start = 0;
  for (const c of Array.from(cel.parentElement!.children) as HTMLTableCellElement[]) {
    if (c === cel) break;
    start += c.colSpan || 1;
  }
  return { start, breed: cel.colSpan || 1 };
}

/** De cel in deze rij die kolom `k` bedekt. */
function celOp(rij: HTMLTableRowElement, k: number): { cel: HTMLTableCellElement; start: number } | null {
  let start = 0;
  for (const c of Array.from(rij.cells)) {
    const breed = c.colSpan || 1;
    if (k >= start && k < start + breed) return { cel: c, start };
    start += breed;
  }
  return null;
}

/** Een lege cel met dezelfde opmaak als het voorbeeld: een label blijft een label. */
function legeCel(voorbeeld: HTMLTableCellElement): HTMLTableCellElement {
  const c = voorbeeld.cloneNode(false) as HTMLTableCellElement;
  c.removeAttribute("colspan");
  c.innerHTML = "<br>";
  return c;
}

export type TabelActie =
  | "rijBoven" | "rijOnder" | "kolomLinks" | "kolomRechts"
  | "rijWeg" | "kolomWeg" | "tabelWeg";

/**
 * Een tabelbewerking rond deze cel, zoals het rechtermuismenu van Word ze kent.
 *
 * <p>Alleen de rijen van déze tabel tellen: het adresvak heeft een tabel in een tabel, en een kolom
 * erbij in de binnenste mag de buitenste niet raken.
 */
export function tabelBewerking(cel: HTMLTableCellElement, actie: TabelActie): void {
  const rij = cel.parentElement as HTMLTableRowElement;
  const tabel = cel.closest("table") as HTMLTableElement;
  const rijen = Array.from(tabel.rows).filter((r) => r.closest("table") === tabel);
  const { start, breed } = kolomVan(cel);

  switch (actie) {
    case "rijBoven":
    case "rijOnder": {
      const nieuw = rij.cloneNode(false) as HTMLTableRowElement;
      for (const c of Array.from(rij.cells)) {
        const n = legeCel(c);
        if (c.colSpan > 1) n.colSpan = c.colSpan;
        nieuw.appendChild(n);
      }
      if (actie === "rijBoven") rij.before(nieuw);
      else rij.after(nieuw);
      return;
    }
    case "kolomLinks":
    case "kolomRechts": {
      const k = actie === "kolomLinks" ? start : start + breed - 1;
      for (const r of rijen) {
        const hier = celOp(r, k);
        if (!hier) {
          r.appendChild(legeCel(r.cells[r.cells.length - 1] ?? cel));
          continue;
        }
        const { cel: c, start: s } = hier;
        const breedte = c.colSpan || 1;
        // Een samengevoegde cel die de nieuwe kolom overspant wordt breder in plaats van gesplitst.
        if (actie === "kolomLinks" && s !== k) c.colSpan = breedte + 1;
        else if (actie === "kolomRechts" && s + breedte - 1 !== k) c.colSpan = breedte + 1;
        else if (actie === "kolomLinks") c.before(legeCel(c));
        else c.after(legeCel(c));
      }
      return;
    }
    case "rijWeg":
      if (rijen.length <= 1) tabel.remove();
      else rij.remove();
      return;
    case "kolomWeg":
      for (const r of rijen) {
        const hier = celOp(r, start);
        if (!hier) continue;
        if ((hier.cel.colSpan || 1) > 1) hier.cel.colSpan -= 1;
        else hier.cel.remove();
        if (!r.cells.length) r.remove();
      }
      if (!Array.from(tabel.rows).some((r) => r.closest("table") === tabel)) tabel.remove();
      return;
    case "tabelWeg":
      tabel.remove();
      return;
  }
}

// ── Tabs ────────────────────────────────────────────────────────────────────────────────────────

/** De afstand tussen twee tabstops, zoals Word hem standaard heeft. */
const TABSTOP_MM = 12.5;

/**
 * Een tab: een leeg vak dat de tekst erna naar de volgende tabstop duwt.
 *
 * <p>Een echt tabteken verdwijnt in HTML (witruimte valt samen), en `tab-size` kent de PDF-renderer
 * niet. Een inline-block met een breedte in millimeters wel — en omdat de editor op ware grootte
 * werkt, staat de tekst in de PDF op dezelfde plek. Niet bewerkbaar, zodat Backspace hem in één keer
 * weghaalt.
 */
function tabHtml(breedMm: number): string {
  return `<span class="db-tab" contenteditable="false" style="display:inline-block;width:${breedMm}mm"></span>`;
}

/** Het blok waarin een tab staat: daar tellen de tabstops vanaf. */
function blokVan(e: Element, win: Window): Element {
  let x: Element | null = e.parentElement;
  while (x && x.parentElement) {
    const d = win.getComputedStyle(x).display;
    if (d !== "inline" && d !== "contents") return x;
    x = x.parentElement;
  }
  return x ?? e;
}

// ── Velden ──────────────────────────────────────────────────────────────────────────────────────

const elementVan = (n: Node): Element | null =>
  n.nodeType === 1 ? (n as Element) : n.parentElement;

/** Een veld zoals de renderer het invult: `{Groep.Veld}`. */
const VELD = /\{[A-Za-z][A-Za-z0-9_]*\.[A-Za-z0-9_.]+\}/g;

/**
 * Maak van elk `{Groep.Veld}` in de tekst één geheel, zoals een veld in Word.
 *
 * <p>Als losse tekst kon de cursor midden in een veld staan: een tab of een letter daar maakte van
 * `{Factuur.TotaalExclBtw}` twee stukken die de renderer allebei niet meer herkent — en op de factuur
 * stond dan letterlijk "{". Als niet-bewerkbaar blokje gaat een veld in zijn geheel mee met slepen,
 * verdwijnt het met één Backspace en kan de cursor er alleen vóór of achter staan. Alleen in de
 * editor: {@link schoneHtml} haalt de omhulling er bij het opslaan weer af.
 */
export function verpakVelden(body: HTMLElement): void {
  const d = body.ownerDocument;
  const loper = d.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
  const teksten: Text[] = [];
  for (let n = loper.nextNode(); n; n = loper.nextNode()) {
    const t = n as Text;
    VELD.lastIndex = 0;
    if (VELD.test(t.data) && !t.parentElement?.closest(".ce-veld")) teksten.push(t);
  }
  const sel = d.getSelection();
  for (const t of teksten) {
    const cursor = sel && sel.isCollapsed && sel.anchorNode === t ? sel.anchorOffset : -1;
    let naCursor: { knoop: Node; plek: number } | null = null;
    const stukken = d.createDocumentFragment();
    let vanaf = 0;
    for (const m of t.data.matchAll(VELD)) {
      const begin = m.index ?? 0;
      if (begin > vanaf) {
        const los = d.createTextNode(t.data.slice(vanaf, begin));
        if (cursor >= vanaf && cursor <= begin && !naCursor) naCursor = { knoop: los, plek: cursor - vanaf };
        stukken.appendChild(los);
      }
      const veld = d.createElement("span");
      veld.className = "ce-veld";
      veld.setAttribute("contenteditable", "false");
      veld.setAttribute("data-token", m[0]);
      veld.title = m[0];
      veld.textContent = kortLabel(m[0]);
      stukken.appendChild(veld);
      vanaf = begin + m[0].length;
      // Stond de cursor in het veld (net de sluitaccolade getypt), dan komt hij er direct achter.
      if (cursor > begin && cursor <= vanaf && !naCursor) naCursor = { knoop: veld, plek: -1 };
    }
    const rest = d.createTextNode(t.data.slice(vanaf));
    if (cursor >= vanaf && !naCursor) naCursor = { knoop: rest, plek: cursor - vanaf };
    stukken.appendChild(rest);
    t.replaceWith(stukken);
    if (naCursor && sel) {
      const r = d.createRange();
      if (naCursor.plek < 0) r.setStartAfter(naCursor.knoop);
      else r.setStart(naCursor.knoop, naCursor.plek);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  }
}

/**
 * Haal de cursor uit een veld: ervoor of erachter, wat het dichtst bij is.
 *
 * <p>Een niet-bewerkbaar blokje houdt typen tegen, maar Chrome zet de cursor er na een klik soms
 * tóch in. Een tab of een ingevoegd veld belandde dan binnen het veld — zo gezien: een lang groen
 * blok met het bedrag helemaal rechts.
 */
export function haalUitVeld(d: Document): void {
  const sel = d.getSelection();
  if (!sel?.rangeCount) return;
  const r = sel.getRangeAt(0);
  const veldVan = (n: Node) => elementVan(n)?.closest(".ce-veld") ?? null;
  const begin = veldVan(r.startContainer);
  const eind = veldVan(r.endContainer);
  if (!begin && !eind) return;
  const nieuw = r.cloneRange();
  if (begin && r.collapsed) {
    const lengte = begin.textContent?.length ?? 0;
    const vooraan = r.startContainer === begin ? r.startOffset === 0 : r.startOffset <= lengte / 2;
    if (vooraan) nieuw.setStartBefore(begin);
    else nieuw.setStartAfter(begin);
    nieuw.collapse(true);
  } else {
    if (begin) nieuw.setStartBefore(begin);
    if (eind) nieuw.setEndAfter(eind);
  }
  sel.removeAllRanges();
  sel.addRange(nieuw);
}

/**
 * Wat er in het blokje van een veld staat: de veldnaam zonder groep en accolades.
 *
 * <p>In een smalle kolom was `{Kostenregel.Product}` veel breder dan wat er straks echt staat
 * ("Gift", "€ 0,00"): de velden liepen over elkaar en de kolommen klopten niet meer. "Product" is
 * genoeg om te zien welk veld het is; de volledige naam staat in de tooltip, en in het sjabloon blijft
 * gewoon `{Kostenregel.Product}` staan (zie {@link schoneHtml}).
 */
export function kortLabel(token: string): string {
  const kaal = token.replace(/^\{|\}$/g, "");
  return kaal.includes(".") ? kaal.slice(kaal.indexOf(".") + 1) : kaal;
}

/**
 * De inhoud van de body zoals hij opgeslagen wordt: zonder de veldomhulling van de editor.
 *
 * <p>Alles wat de editor zelf toevoegt aan de body gaat hier weer af. Zo krijgt de renderer precies
 * de `{Groep.Veld}`-tekst die hij kent, en ziet een sjabloon er na opslaan hetzelfde uit als ervoor.
 */
export function schoneHtml(body: HTMLElement): string {
  if (!body.querySelector(".ce-veld")) return body.innerHTML;
  const kopie = body.cloneNode(true) as HTMLElement;
  for (const v of Array.from(kopie.querySelectorAll(".ce-veld"))) {
    v.replaceWith(body.ownerDocument.createTextNode(v.getAttribute("data-token") ?? v.textContent ?? ""));
  }
  kopie.normalize();
  return kopie.innerHTML;
}

// ── Kleur en inspringen ─────────────────────────────────────────────────────────────────────────

const isHouderEl = (e: Element | null, body: HTMLElement) =>
  !!e && (e === body || e.classList.contains("db-doc") || e.hasAttribute("data-repeat"));

/**
 * Waar een achtergrondkleur op komt: de cellen in de selectie, of anders het blok van de cursor.
 *
 * <p>"Cel" is alles wat zich als cel gedraagt — een echte tabelcel, maar ook de label- en
 * bedragvakken van de totalen, die met `display: table-cell` zijn opgebouwd.
 */
export function kleurDoelen(bereik: Range, body: HTMLElement): HTMLElement[] {
  const win = (body.ownerDocument.defaultView ?? window) as Window;
  const isCel = (e: Element) =>
    e.tagName === "TD" || e.tagName === "TH" || win.getComputedStyle(e).display === "table-cell";
  const celVan = (e: Element | null): HTMLElement | null => {
    for (let x = e; x && x !== body; x = x.parentElement) if (isCel(x)) return x as HTMLElement;
    return null;
  };
  const begin = elementVan(bereik.startContainer);
  const eind = elementVan(bereik.endContainer);
  const eerste = celVan(begin);
  if (bereik.collapsed || eerste === celVan(eind)) {
    if (eerste) return [eerste];
    for (let x = begin; x && !isHouderEl(x, body); x = x.parentElement) {
      if (win.getComputedStyle(x).display !== "inline") return [x as HTMLElement];
    }
    return [];
  }
  const cellen = (Array.from(body.querySelectorAll("*")) as HTMLElement[])
    .filter((e) => isCel(e) && bereik.intersectsNode(e) && !e.closest(".db-header, .db-footer, #ce-lagen"));
  // Alleen de binnenste: een adresvak met een tabel erin kleurt de cellen, niet het hele vak.
  return cellen.filter((c) => !cellen.some((o) => o !== c && c.contains(o)));
}

/** Kleur de doelen, of haal de kleur weg — ook een kleur die uit de afdrukstijl komt (de gele balk). */
export function zetAchtergrond(doelen: HTMLElement[], kleur: string | null): void {
  for (const e of doelen) {
    e.style.removeProperty("background-color");
    if (kleur) {
      e.style.backgroundColor = kleur;
      continue;
    }
    const win = (e.ownerDocument.defaultView ?? window) as Window;
    const rest = win.getComputedStyle(e).backgroundColor;
    if (rest && rest !== "transparent" && !/rgba\(.*,\s*0\)$/.test(rest)) {
      e.style.backgroundColor = "transparent";
    }
  }
}

/**
 * Het blok dat inspringt: het buitenste blok onder de pagina, zoals een alinea, een tabel of de
 * totalen. Een cel springt niet los in; dan schuift de hele tabel, zoals in Word.
 */
export function inspringBlok(bereik: Range, body: HTMLElement): HTMLElement | null {
  const win = (body.ownerDocument.defaultView ?? window) as Window;
  let blok: HTMLElement | null = null;
  for (let x = elementVan(bereik.startContainer); x && !isHouderEl(x, body); x = x.parentElement) {
    if (x.closest(".db-header, .db-footer") && !isKopOfVoet(x)) continue;
    if (win.getComputedStyle(x).display !== "inline") blok = x as HTMLElement;
  }
  return blok;
}

/** Laat een blok één tabstop in- of terugspringen, zonder dat het buiten de pagina loopt. */
export function springIn(blok: HTMLElement, richting: 1 | -1): void {
  const d = blok.ownerDocument;
  const win = (d.defaultView ?? window) as Window;
  const mm = px(d, "100mm") / 100 || 3.7795;
  // Een eerder gezette inspringing staat in mm; de rest komt uit de afdrukstijl (in pixels gemeten).
  const oud = blok.style.marginLeft.endsWith("mm")
    ? parseFloat(blok.style.marginLeft) * mm
    : parseFloat(win.getComputedStyle(blok).marginLeft) || 0;
  const nieuw = Math.max(0, oud + richting * TABSTOP_MM * mm);
  const ouder = blok.parentElement!;
  const os = win.getComputedStyle(ouder);
  const binnen = ouder.getBoundingClientRect().right - (parseFloat(os.paddingRight) || 0);
  const breedte = ouder.clientWidth - (parseFloat(os.paddingLeft) || 0) - (parseFloat(os.paddingRight) || 0);
  blok.style.marginLeft = `${(nieuw / mm).toFixed(1)}mm`;
  // Een blok met een vaste breedte (een tabel van 100%) zou over de rechterrand schuiven: dan wordt
  // hij smaller. Terugspringen geeft die ruimte weer terug.
  if (breedte > 0 && blok.getBoundingClientRect().right > binnen + 0.5) {
    blok.style.width = `${Math.max(10, ((breedte - nieuw) / breedte) * 100).toFixed(1)}%`;
  } else if (richting < 0 && blok.style.width.endsWith("%") && breedte > 0) {
    const pct = parseFloat(blok.style.width) + ((oud - nieuw) / breedte) * 100;
    blok.style.width = `${Math.min(100, pct).toFixed(1)}%`;
  }
}

// ── De lagen ────────────────────────────────────────────────────────────────────────────────────

const LAAG_CSS = `
#ce-vellen { position: absolute; top: 0; left: 0; width: 100%; height: 0; z-index: -1;
  pointer-events: none; }
#ce-vellen .ce-vel { position: absolute; background: #fff;
  box-shadow: 0 1px 3px rgba(16,24,40,.16), 0 4px 14px rgba(16,24,40,.08); }
/* De voettekst op de andere vellen: een kopie, in de ondermarge, net als op papier. Bewerken doe je
   op het laatste vel; dezelfde tekst staat op elke pagina. */
#ce-vellen .db-footer { position: absolute !important; top: auto !important; left: ${MARGE} !important;
  right: ${MARGE} !important; bottom: calc(${MARGE} - ${VOET_ONDER}) !important; opacity: .7; }
/* De koptekst op de andere vellen: net zo, bovenaan. */
#ce-vellen .db-header { position: absolute !important; bottom: auto !important; left: ${MARGE} !important;
  right: ${MARGE} !important; top: ${KOP_BOVEN_MM}mm !important; opacity: .7; }
/* Een veld: één geheel, als een veld in Word. Geen opvulling, zodat de regel even breed blijft als
   in de PDF. */
/* Past de veldnaam nog niet in zijn kolom, dan kapt hij af met … in plaats van over de buurcel
   te lopen. */
.ce-veld { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; vertical-align: bottom;
  background: #e4f0e9; border-radius: 3px; box-shadow: inset 0 0 0 1px #c9ded1;
  cursor: default; }
#ce-lagen { position: absolute; top: 0; left: 0; width: 100%; height: 0; pointer-events: none;
  z-index: 50; font: 12px/1.3 Helvetica, Arial, sans-serif; }
#ce-lagen .ce-blokknoppen { position: absolute; display: none; pointer-events: auto; gap: 2px;
  background: #fff; border: 1px solid #cfd6dc; border-radius: 7px; padding: 2px;
  box-shadow: 0 2px 8px rgba(16,24,40,.14); }
#ce-lagen .ce-blokknoppen button { all: unset; cursor: pointer; width: 22px; height: 22px;
  display: inline-flex; align-items: center; justify-content: center; border-radius: 5px;
  color: #344054; font-size: 13px; }
#ce-lagen .ce-blokknoppen button:hover { background: #e7f0ec; color: #2f5d4a; }
#ce-lagen .ce-blokknoppen button[disabled] { opacity: .3; cursor: default; background: none; }
#ce-lagen .ce-blokknoppen button.ce-weg:hover { background: #fdecea; color: #b42318; }
#ce-lagen .ce-menu { position: absolute; display: none; pointer-events: auto; min-width: 190px;
  background: #fff; border: 1px solid #d0d5dd; border-radius: 8px; padding: 4px;
  box-shadow: 0 8px 24px rgba(16,24,40,.18); }
#ce-lagen .ce-menu button { all: unset; box-sizing: border-box; display: block; width: 100%;
  cursor: pointer; padding: 6px 10px; border-radius: 5px; color: #1d2939; }
#ce-lagen .ce-menu button:hover { background: #eef4f1; }
#ce-lagen .ce-menu button.ce-weg { color: #b42318; }
#ce-lagen .ce-menu button.ce-weg:hover { background: #fdecea; }
#ce-lagen .ce-menu hr { border: 0; border-top: 1px solid #eaecf0; margin: 4px 2px; }
#ce-lagen .ce-kleuren { display: flex; align-items: center; gap: 5px; padding: 6px 10px 4px; }
#ce-lagen .ce-kleuren span { color: #475467; margin-right: 2px; }
#ce-lagen .ce-kleuren button { all: unset; box-sizing: border-box; cursor: pointer; width: 18px;
  height: 18px; border-radius: 4px; border: 1px solid rgba(16,24,40,.25); padding: 0; }
#ce-lagen .ce-kleuren button:hover { outline: 2px solid #2f5d4a; outline-offset: 1px; }
#ce-lagen .ce-kleuren button.ce-geen { background: linear-gradient(to top right, #fff 45%, #d92d20 47%,
  #d92d20 53%, #fff 55%); }
#ce-lagen .ce-kleuren label { position: relative; width: 18px; height: 18px; border-radius: 4px;
  border: 1px dashed #98a2b3; display: inline-flex; align-items: center; justify-content: center;
  color: #475467; cursor: pointer; font-size: 13px; line-height: 1; }
#ce-lagen .ce-kleuren input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
/* Een rand verslepen: de lijn waar hij komt en hoe breed het wordt. */
#ce-lagen .ce-gids { position: absolute; display: none; width: 0; border-left: 1.5px dashed #2f5d4a; }
#ce-lagen .ce-gids span { position: absolute; top: -20px; left: 4px; background: #2f5d4a; color: #fff;
  border-radius: 4px; padding: 1px 6px; font-size: 11px; white-space: nowrap; }
html.ce-sleepbaar, html.ce-sleepbaar * { cursor: col-resize !important; }
/* Een plaatje groter of kleiner maken: een kader eromheen en een greep rechtsonder, zoals in Word. */
#ce-lagen .ce-beeldrand { position: absolute; display: none; border: 1.5px solid #2f5d4a;
  box-sizing: border-box; }
#ce-lagen .ce-beeldgreep { position: absolute; display: none; width: 12px; height: 12px;
  margin: -6px 0 0 -6px; background: #fff; border: 2px solid #2f5d4a; border-radius: 3px;
  box-sizing: border-box; cursor: nwse-resize; pointer-events: auto; }
html.ce-beeldsleep, html.ce-beeldsleep * { cursor: nwse-resize !important; }
/* Het handvat aan de rechterrand van een tabel of de totalen: zichtbaar zodra de muis erboven is,
   zodat niemand hoeft te raden dat die rand te verslepen is. */
#ce-lagen .ce-handvat { position: absolute; display: none; width: 8px; margin-left: -4px;
  border-radius: 4px; background: rgba(47,93,74,.16); border: 1px solid rgba(47,93,74,.5);
  cursor: col-resize; pointer-events: auto; box-sizing: border-box; }
#ce-lagen .ce-handvat::after { content: ""; position: absolute; left: 2px; right: 2px; top: 50%;
  height: 14px; margin-top: -7px; border-left: 1px solid #2f5d4a; border-right: 1px solid #2f5d4a; }
#ce-lagen .ce-handvat:hover { background: rgba(47,93,74,.38); }
#ce-lagen .ce-melding { position: fixed; left: 50%; bottom: 14px; transform: translateX(-50%);
  display: none; pointer-events: auto; background: #1f2937; color: #fff; border-radius: 8px;
  padding: 7px 10px 7px 12px; box-shadow: 0 4px 14px rgba(0,0,0,.25); white-space: nowrap; }
#ce-lagen .ce-melding button { all: unset; cursor: pointer; margin-left: 10px; color: #a7f3d0;
  font-weight: 600; }
`;

const MENU: { actie: TabelActie | "knip" | "kopieer"; label: string; weg?: boolean; lijnVoor?: boolean }[] = [
  { actie: "knip", label: "Knippen" },
  { actie: "kopieer", label: "Kopiëren" },
  { actie: "rijBoven", label: "Rij boven invoegen", lijnVoor: true },
  { actie: "rijOnder", label: "Rij onder invoegen" },
  { actie: "kolomLinks", label: "Kolom links invoegen" },
  { actie: "kolomRechts", label: "Kolom rechts invoegen" },
  { actie: "rijWeg", label: "Rij verwijderen", weg: true, lijnVoor: true },
  { actie: "kolomWeg", label: "Kolom verwijderen", weg: true },
  { actie: "tabelWeg", label: "Tabel verwijderen", weg: true },
];

/** De celkleuren in het rechtermuismenu: het geel van de afdrukstijl en een paar rustige tinten. */
const CELKLEUREN: { kleur: string; naam: string }[] = [
  { kleur: "#dede00", naam: "Geel (standaard)" },
  { kleur: "#f6f6cf", naam: "Lichtgeel" },
  { kleur: "#d9ead3", naam: "Lichtgroen" },
  { kleur: "#1f9d57", naam: "Groen" },
  { kleur: "#cfe2f3", naam: "Lichtblauw" },
  { kleur: "#e5e7eb", naam: "Grijs" },
];

const MELDING: Partial<Record<TabelActie, string>> = {
  rijWeg: "Rij verwijderd",
  kolomWeg: "Kolom verwijderd",
  tabelWeg: "Tabel verwijderd",
};

/**
 * Hang de lagen aan het document. `bijWijziging` wordt aangeroepen na elke bewerking die niet uit
 * typen komt (een herhaalblok verplaatsen, een rij erbij), met de nieuwe inhoud van de body — zodat
 * het concept net zo wordt bijgewerkt als bij typen.
 */
export function hangLagen(d: Document, opties: {
  alleenLezen: boolean;
  bijWijziging: (html: string) => void;
}): Lagen {
  const body = d.body;
  const html = d.documentElement;
  const win = (d.defaultView ?? window) as Window;

  const stijl = d.createElement("style");
  stijl.textContent = LAAG_CSS;
  d.head.appendChild(stijl);
  // Het doorschuiven naar een volgend vel: extra ruimte boven een blok, als regel in de <head>.
  const verschuif = d.createElement("style");
  d.head.appendChild(verschuif);

  const vellen = d.createElement("div");
  vellen.id = "ce-vellen";
  vellen.setAttribute("aria-hidden", "true");
  html.appendChild(vellen);

  const lagen = d.createElement("div");
  lagen.id = "ce-lagen";
  lagen.setAttribute("contenteditable", "false");
  html.appendChild(lagen);

  // ── Herhaalblokknoppen ─────────────────────────────────────────────────────────────────────
  const knoppen = d.createElement("div");
  knoppen.className = "ce-blokknoppen";
  knoppen.setAttribute("role", "toolbar");
  knoppen.setAttribute("aria-label", "Herhaalblok");
  const knop = (teken: string, titel: string, klasse = "") => {
    const b = d.createElement("button");
    b.type = "button";
    b.textContent = teken;
    b.title = titel;
    b.setAttribute("aria-label", titel);
    if (klasse) b.className = klasse;
    knoppen.appendChild(b);
    return b;
  };
  const op = knop("↑", "Herhaalblok omhoog");
  const neer = knop("↓", "Herhaalblok omlaag");
  const dubbel = knop("⧉", "Herhaalblok dupliceren");
  const weg = knop("✕", "Herhaalblok verwijderen", "ce-weg");
  lagen.appendChild(knoppen);

  // ── Het rechtermuismenu ────────────────────────────────────────────────────────────────────
  // Knippen en kopiëren bij een selectie, rijen en kolommen in een tabel, en altijd de arcering:
  // de achtergrondkleur van de cel of het blok onder de muis — ook het geel van de afdrukstijl.
  const menu = d.createElement("div");
  menu.className = "ce-menu";
  menu.setAttribute("role", "menu");
  menu.setAttribute("aria-label", "Bewerken");
  const menuKnoppen = new Map<string, HTMLButtonElement>();
  for (const m of MENU) {
    if (m.lijnVoor) menu.appendChild(d.createElement("hr"));
    const b = d.createElement("button");
    b.type = "button";
    b.setAttribute("role", "menuitem");
    b.textContent = m.label;
    if (m.weg) b.className = "ce-weg";
    menuKnoppen.set(m.actie, b);
    menu.appendChild(b);
  }
  const kleurLijn = d.createElement("hr");
  kleurLijn.className = "ce-kleurlijn";
  menu.appendChild(kleurLijn);
  const kleurRij = d.createElement("div");
  kleurRij.className = "ce-kleuren";
  kleurRij.setAttribute("role", "group");
  kleurRij.setAttribute("aria-label", "Arcering");
  const kleurLabel = d.createElement("span");
  kleurLabel.textContent = "Arcering";
  kleurRij.appendChild(kleurLabel);
  menu.appendChild(kleurRij);
  lagen.appendChild(menu);

  // ── De melding met "ongedaan maken" ────────────────────────────────────────────────────────
  const melding = d.createElement("div");
  melding.className = "ce-melding";
  melding.setAttribute("role", "status");
  const meldingTekst = d.createElement("span");
  const terug = d.createElement("button");
  terug.type = "button";
  terug.textContent = "Ongedaan maken";
  melding.append(meldingTekst, terug);
  lagen.appendChild(melding);

  let actief: HTMLElement | null = null;
  let menuCel: HTMLTableCellElement | null = null;
  let menuBereik: Range | null = null;
  // De inhoud vóór de laatste bewerking die niet uit typen kwam. De ongedaan-maken van de browser
  // kent alleen typen; deze vangt de rest, tot er weer getypt wordt.
  let vorige: string | null = null;
  let meldingTimer: number | undefined;
  let gepland = 0;
  let klaar = false;
  // Hoeveel het document omlaag staat om de koptekst voor de volgende pagina's boven het eerste vel te tonen.
  let extraBoven = 0;

  const scrollY = () => d.defaultView?.scrollY ?? 0;
  const scrollX = () => d.defaultView?.scrollX ?? 0;

  // ── De vellen ──────────────────────────────────────────────────────────────────────────────
  const herbereken = () => {
    gepland = 0;
    // Een net ingevoegd of getypt veld wordt ook een blokje.
    verpakVelden(body);
    const hoogte = px(d, VEL_HOOGTE);
    const marge = px(d, MARGE);
    const voetOnder = px(d, VOET_ONDER);
    if (!hoogte) return;
    const stap = hoogte + TUSSEN_VELLEN;
    // Waar ook in het document, net als de PDF-generator: een overgenomen document zet de voettekst soms
    // een laag dieper dan direct onder db-doc.
    const voet = kopVoetVan(body, "voet");
    const kop = kopVoetVan(body, "kop");
    const kopEerste = kopVoetVan(body, "kopEerste");
    const mmPx = px(d, "100mm") / 100;
    const hoogteMm = (e: HTMLElement | null) => (e && mmPx ? e.offsetHeight / mmPx : 0);
    const voetMm = hoogteMm(voet);
    // De ondermarge: gewoon 2 cm, of genoeg voor een hoge voettekst plus wat lucht erboven.
    const margeOnder = voetMm > VOET_RUIM_MM ? (VOET_BODEM_MM + voetMm + VOET_LUCHT_MM) * mmPx : marge;
    // De bovenmarge: 2 cm, of genoeg voor de koptekst plus lucht eronder. Pagina 1 kan een eigen koptekst hebben.
    const bovenVoor = (e: HTMLElement | null) =>
      (e ? Math.max(marge, (KOP_BOVEN_MM + hoogteMm(e) + KOP_LUCHT_MM) * mmPx) : marge);
    const margeBovenEerste = bovenVoor(kopEerste ?? kop);
    const margeBovenRest = bovenVoor(kop);
    html.style.setProperty("--ce-boven", `${margeBovenEerste}px`);
    const vlak = hoogte - Math.max(margeBovenEerste, margeBovenRest) - margeOnder;

    // Tabs opnieuw uitlijnen, zoals Word dat doet: verandert de tekst ervoor, dan komt de tekst erna
    // toch weer op de volgende tabstop. De breedte staat in de HTML, dus de PDF volgt vanzelf.
    const mm = px(d, "100mm") / 100;
    let tabsAnders = false;
    for (const tab of Array.from(body.querySelectorAll<HTMLElement>(".db-tab"))) {
      const oud = tab.style.width;
      tab.style.width = "0px";
      const blok = blokVan(tab, win);
      const bs = win.getComputedStyle(blok);
      const links = blok.getBoundingClientRect().left
        + (parseFloat(bs.paddingLeft) || 0) + (parseFloat(bs.borderLeftWidth) || 0);
      const stop = TABSTOP_MM * mm;
      let breed = stop - ((tab.getBoundingClientRect().left - links) % stop);
      if (breed < 1.5 * mm) breed += stop;
      tab.style.width = `${(breed / mm).toFixed(1)}mm`;
      if (tab.style.width !== oud) tabsAnders = true;
    }
    // Bij het openen niet: dan zou een ongewijzigd sjabloon meteen als gewijzigd gelden.
    if (tabsAnders && klaar) opties.bijWijziging(schoneHtml(body));

    verschuif.textContent = "";
    const regels = new Map<string, number>();
    const schrijf = () => {
      verschuif.textContent = [...regels]
        .map(([k, m]) => `${k} { margin-top: ${m.toFixed(2)}px !important; }`)
        .join("\n");
    };

    const br = body.getBoundingClientRect();
    const boven = br.top + scrollY();
    const velVan = (y: number) => Math.max(0, Math.floor((y - boven) / stap));
    const inhoudBoven = (i: number) => boven + i * stap + (i === 0 ? margeBovenEerste : margeBovenRest);
    const inhoudOnder = (i: number) => boven + i * stap + hoogte - margeOnder;

    const stukken: HTMLElement[] = [];
    stroom(d.querySelector(".db-doc") ?? body, vlak, win, stukken);

    // Van boven naar beneden: past een stuk niet meer op zijn vel, dan begint het bovenaan het
    // volgende. Na een handmatig pagina-einde begint het volgende stuk hoe dan ook op een nieuw vel.
    let naEinde: number | null = null;
    let onderste = boven + margeBovenEerste;
    for (const e of stukken) {
      const r = e.getBoundingClientRect();
      const top = r.top + scrollY();
      const onder = r.bottom + scrollY();
      const vel = velVan(top);
      let doel: number | null = null;
      if (naEinde !== null && top < inhoudBoven(naEinde + 1)) doel = inhoudBoven(naEinde + 1);
      else if (onder > inhoudOnder(vel) + 0.5 && r.height <= vlak) doel = inhoudBoven(vel + 1);

      if (doel !== null) {
        const k = kiezer(e, body);
        let marge0 = parseFloat(win.getComputedStyle(e).marginTop) || 0;
        let nu = top;
        // Marges vallen samen met die van de buren; meten en bijstellen tot hij goed staat.
        for (let poging = 0; poging < 3 && Math.abs(doel - nu) > 0.5; poging++) {
          marge0 += doel - nu;
          regels.set(k, marge0);
          schrijf();
          nu = e.getBoundingClientRect().top + scrollY();
        }
      }
      naEinde = isPaginaEinde(e) ? velVan(e.getBoundingClientRect().top + scrollY()) : null;
      onderste = Math.max(onderste, e.getBoundingClientRect().bottom + scrollY());
    }

    const aantal = Math.max(1, velVan(onderste - 1) + 1, naEinde !== null ? naEinde + 2 : 0);
    html.style.setProperty("--ce-body-hoogte", `${aantal * hoogte + (aantal - 1) * TUSSEN_VELLEN}px`);

    // Heeft pagina 1 een eigen koptekst en is er maar één vel, dan staat de koptekst voor de volgende
    // pagina's boven het eerste vel, op het bureau: zo kun je hem toch bewerken. Het document schuift
    // daarvoor omlaag, en dat vraagt een nieuwe meting.
    const nodigBoven = kop && kopEerste && aantal === 1 ? kop.offsetHeight + 40 : 0;
    if (nodigBoven !== extraBoven) {
      extraBoven = nodigBoven;
      html.style.setProperty("--ce-extra-boven", `${nodigBoven}px`);
      plan();
      return;
    }

    // De echte voettekst onderaan het laatste vel; kopieën onderaan de andere.
    if (voet) {
      const voetTop = (aantal - 1) * stap + hoogte - marge + voetOnder - voet.offsetHeight;
      html.style.setProperty("--ce-voet-top", `${voetTop}px`);
    }
    // De koptekst van pagina 1 bovenaan het eerste vel. De koptekst voor elke pagina bovenaan het eerste vel,
    // of — als pagina 1 een eigen koptekst heeft — bovenaan het tweede, of boven het eerste op het bureau.
    const kopTop = KOP_BOVEN_MM * mmPx;
    if (kopEerste) html.style.setProperty("--ce-kop-eerste-top", `${kopTop}px`);
    if (kop) {
      const top = !kopEerste ? kopTop : aantal > 1 ? stap + kopTop : -(kop.offsetHeight + 24);
      html.style.setProperty("--ce-kop-top", `${top}px`);
    }
    const echteKopVel = kopEerste ? 1 : 0;

    vellen.textContent = "";
    const bs = win.getComputedStyle(body);
    vellen.style.fontFamily = bs.fontFamily;
    vellen.style.color = bs.color;
    vellen.style.lineHeight = bs.lineHeight;
    for (let i = 0; i < aantal; i++) {
      const vel = d.createElement("div");
      vel.className = "ce-vel";
      vel.style.cssText = `left:${br.left + scrollX()}px;top:${boven + i * stap}px;`
        + `width:${br.width}px;height:${hoogte}px`;
      if (voet && i < aantal - 1) {
        const kopie = voet.cloneNode(true) as HTMLElement;
        kopie.removeAttribute("contenteditable");
        vel.appendChild(kopie);
      }
      // De koptekst op de andere vellen: een kopie, net als de voettekst. Bewerken doe je op het vel met de echte.
      if (kop && i >= 1 && i !== echteKopVel) {
        const kopie = kop.cloneNode(true) as HTMLElement;
        kopie.removeAttribute("contenteditable");
        vel.appendChild(kopie);
      }
      vellen.appendChild(vel);
    }

    if (actief && !actief.isConnected) verberg();
    else if (actief) toon(actief);
    // Het handvat volgt zijn blok na een Tab, een Ctrl+Z of een vel erbij.
    toonHandvat(handvatBlok?.isConnected ? handvatBlok : null);
    toonBeeld(beeld?.isConnected ? beeld : null);
  };

  // Typen roept dit bij elke aanslag aan; één keer per korte pauze is genoeg. Een timer en geen
  // requestAnimationFrame: die slaat over zolang het tabblad op de achtergrond staat.
  const plan = () => {
    const w = d.defaultView;
    if (!w) return herbereken();
    if (!gepland) gepland = w.setTimeout(herbereken, 30);
  };

  // ── Herhaalblokken ─────────────────────────────────────────────────────────────────────────
  const buur = (b: Element, richting: "op" | "neer"): Element | null => {
    let e = richting === "op" ? b.previousElementSibling : b.nextElementSibling;
    while (e && isKopOfVoet(e)) e = richting === "op" ? e.previousElementSibling : e.nextElementSibling;
    return e;
  };

  const verberg = () => {
    actief = null;
    knoppen.style.display = "none";
  };

  const toon = (b: HTMLElement) => {
    actief = b;
    const r = b.getBoundingClientRect();
    knoppen.style.display = "inline-flex";
    const breedte = knoppen.getBoundingClientRect().width || 100;
    knoppen.style.top = `${Math.max(2, r.top + scrollY() - 14)}px`;
    knoppen.style.left = `${Math.max(4, r.right + scrollX() - breedte - 6)}px`;
    op.disabled = !buur(b, "op");
    neer.disabled = !buur(b, "neer");
  };

  const naWijziging = () => {
    opties.bijWijziging(schoneHtml(body));
    herbereken();
  };

  const toonMelding = (tekst: string, terugTeDraaien = true) => {
    meldingTekst.textContent = tekst;
    terug.style.display = terugTeDraaien ? "" : "none";
    melding.style.display = "block";
    if (meldingTimer) d.defaultView?.clearTimeout(meldingTimer);
    meldingTimer = d.defaultView?.setTimeout(() => {
      melding.style.display = "none";
    }, 7000);
  };

  /** Een bewerking die de browser zelf niet kan terugdraaien: onthoud wat er stond. */
  const bewerk = (werk: () => void, tekst?: string) => {
    vorige = body.innerHTML;
    werk();
    naWijziging();
    if (tekst) toonMelding(tekst);
  };

  const zetTerug = () => {
    if (vorige === null) return false;
    body.innerHTML = vorige;
    vorige = null;
    melding.style.display = "none";
    verberg();
    naWijziging();
    return true;
  };

  const opBeweging = (e: MouseEvent) => {
    if (opties.alleenLezen) return;
    const doel = e.target as Element | null;
    if (doel && lagen.contains(doel)) return; // boven de knoppen zelf: laten staan
    const b = doel?.closest?.("[data-repeat]") as HTMLElement | null;
    if (b && body.contains(b)) toon(b);
    else verberg();
  };

  // De selectie in de tekst mag niet wegspringen door een klik op een knop of een menu-item.
  for (const el of [knoppen, menu, melding]) el.addEventListener("mousedown", (e) => e.preventDefault());

  op.addEventListener("click", () => {
    const b = actief;
    const v = b && buur(b, "op");
    if (b && v) bewerk(() => v.before(b));
  });
  neer.addEventListener("click", () => {
    const b = actief;
    const v = b && buur(b, "neer");
    if (b && v) bewerk(() => v.after(b));
  });
  dubbel.addEventListener("click", () => {
    const b = actief;
    if (b) bewerk(() => b.after(b.cloneNode(true)));
  });
  weg.addEventListener("click", () => {
    const b = actief;
    if (!b) return;
    bewerk(() => b.remove(), "Herhaalblok verwijderd");
    verberg();
  });
  terug.addEventListener("click", () => zetTerug());

  // ── Het tabelmenu ──────────────────────────────────────────────────────────────────────────
  const sluitMenu = () => {
    menu.style.display = "none";
    menuCel = null;
    menuBereik = null;
  };

  const kleurMenu = (kleur: string | null) => {
    const bereik = menuBereik;
    sluitMenu();
    if (!bereik) return;
    const doelen = kleurDoelen(bereik, body);
    if (doelen.length) bewerk(() => zetAchtergrond(doelen, kleur));
  };
  for (const k of CELKLEUREN) {
    const b = d.createElement("button");
    b.type = "button";
    b.title = k.naam;
    b.setAttribute("aria-label", `Achtergrond ${k.naam.toLowerCase()}`);
    b.style.background = k.kleur;
    b.addEventListener("click", () => kleurMenu(k.kleur));
    kleurRij.appendChild(b);
  }
  const geen = d.createElement("button");
  geen.type = "button";
  geen.className = "ce-geen";
  geen.title = "Geen achtergrondkleur";
  geen.setAttribute("aria-label", "Geen achtergrondkleur");
  geen.addEventListener("click", () => kleurMenu(null));
  kleurRij.appendChild(geen);
  // Elke andere kleur: de kleurkiezer van de browser.
  const meer = d.createElement("label");
  meer.title = "Andere kleur…";
  meer.textContent = "+";
  const kleurKiezer = d.createElement("input");
  kleurKiezer.type = "color";
  kleurKiezer.setAttribute("aria-label", "Andere achtergrondkleur");
  kleurKiezer.addEventListener("change", () => kleurMenu(kleurKiezer.value));
  meer.appendChild(kleurKiezer);
  kleurRij.appendChild(meer);

  const opMenu = (e: MouseEvent) => {
    if (opties.alleenLezen) return;
    const doel = e.target as Element | null;
    if (!doel || !body.contains(doel) || lagen.contains(doel)) return sluitMenu();
    e.preventDefault();
    const cel = doel.closest("td, th") as HTMLTableCellElement | null;
    menuCel = cel && body.contains(cel) ? cel : null;
    // De kleur gaat naar de selectie als je daarin klikt, anders naar wat onder de muis staat.
    const sel = d.getSelection();
    const selectie = !!sel && !sel.isCollapsed && sel.rangeCount > 0;
    if (selectie && sel!.getRangeAt(0).intersectsNode(doel)) {
      menuBereik = sel!.getRangeAt(0).cloneRange();
    } else {
      menuBereik = d.createRange();
      menuBereik.selectNodeContents(doel);
      menuBereik.collapse(true);
    }
    menuKnoppen.get("knip")!.style.display = selectie ? "" : "none";
    menuKnoppen.get("kopieer")!.style.display = selectie ? "" : "none";
    for (const [actie, knop] of menuKnoppen) {
      if (actie !== "knip" && actie !== "kopieer") knop.style.display = menuCel ? "" : "none";
    }
    const lijnen = Array.from(menu.querySelectorAll("hr")) as HTMLElement[];
    lijnen.forEach((l, i) => {
      // De eerste lijn scheidt knippen van de tabel, de tweede de tabel van zichzelf.
      l.style.display = i === 0 ? (selectie && menuCel ? "" : "none") : menuCel ? "" : "none";
    });
    kleurLijn.style.display = selectie || menuCel ? "" : "none";
    menu.style.display = "block";
    const breed = menu.getBoundingClientRect().width || 200;
    const hoog = menu.getBoundingClientRect().height || 280;
    const vw = d.defaultView?.innerWidth ?? 800;
    const vh = d.defaultView?.innerHeight ?? 600;
    menu.style.left = `${Math.min(e.clientX, vw - breed - 4) + scrollX()}px`;
    menu.style.top = `${Math.min(e.clientY, Math.max(4, vh - hoog - 4)) + scrollY()}px`;
  };

  for (const m of MENU) {
    menuKnoppen.get(m.actie)!.addEventListener("click", () => {
      const cel = menuCel;
      sluitMenu();
      if (!cel) return;
      if (m.actie === "knip" || m.actie === "kopieer") {
        d.execCommand(m.actie === "knip" ? "cut" : "copy");
        return;
      }
      const actie = m.actie;
      bewerk(() => tabelBewerking(cel, actie), MELDING[actie]);
    });
  }

  // ── Tab, zoals in Word ─────────────────────────────────────────────────────────────────────
  // Zonder dit springt de focus met Tab het document uit, naar het volgende veld van het scherm.
  const zetCursor = (knoop: Node, begin: boolean) => {
    const sel = d.getSelection();
    if (!sel) return;
    const r = d.createRange();
    r.selectNodeContents(knoop);
    r.collapse(begin);
    sel.removeAllRanges();
    sel.addRange(r);
  };

  /** In een tabel: naar de volgende (of vorige) cel; na de laatste cel komt er een rij bij. */
  const naarCel = (cel: HTMLTableCellElement, terug: boolean) => {
    const tabel = cel.closest("table")!;
    const cellen = (Array.from(tabel.querySelectorAll("td, th")) as HTMLTableCellElement[])
      .filter((c) => c.closest("table") === tabel);
    let doel: HTMLTableCellElement | undefined = cellen[cellen.indexOf(cel) + (terug ? -1 : 1)];
    if (!doel && !terug) {
      bewerk(() => tabelBewerking(cel, "rijOnder"));
      doel = (cel.parentElement!.nextElementSibling as HTMLTableRowElement | null)?.cells[0];
    }
    if (doel) zetCursor(doel, true);
  };

  // Zelf invoegen en niet via execCommand("insertHTML"): Chrome splitst dan bij een tweede tab de
  // omringende spans en zet stukken van de regel binnenstebuiten (zo gezien in de totalen).
  const voegTabIn = () => {
    haalUitVeld(d);
    const sel = d.getSelection();
    if (!sel?.rangeCount) return;
    const r = sel.getRangeAt(0);
    bewerk(() => {
      r.deleteContents();
      const houder = d.createElement("div");
      houder.innerHTML = tabHtml(TABSTOP_MM);
      const tab = houder.firstChild!;
      r.insertNode(tab);
      r.setStartAfter(tab);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    });
  };

  /** De tab direct vóór de cursor, als die er staat. */
  const tabVoorCursor = (): Element | null => {
    const sel = d.getSelection();
    if (!sel?.rangeCount || !sel.isCollapsed) return null;
    const r = sel.getRangeAt(0);
    let n: Node | null;
    if (r.startContainer.nodeType === Node.TEXT_NODE) {
      if (r.startOffset > 0) return null;
      n = r.startContainer.previousSibling;
    } else {
      n = r.startContainer.childNodes[r.startOffset - 1] ?? null;
    }
    while (n && n.nodeType === Node.TEXT_NODE && !n.textContent) n = n.previousSibling;
    // Geen instanceof: de knopen komen uit het iframe, en dat heeft zijn eigen Element.
    return n && n.nodeType === Node.ELEMENT_NODE && (n as Element).classList.contains("db-tab")
      ? (n as Element) : null;
  };

  const opTab = (e: KeyboardEvent) => {
    const sel = d.getSelection();
    const plek = sel?.anchorNode ?? null;
    if (!plek || !body.contains(plek)) return;
    e.preventDefault();
    const el = plek.nodeType === Node.ELEMENT_NODE ? (plek as Element) : plek.parentElement;
    const cel = el?.closest("td, th") as HTMLTableCellElement | null;
    if (cel && body.contains(cel)) return naarCel(cel, e.shiftKey);
    if (el && staatRechts(el)) return schuifRechts(el, e.shiftKey ? -1 : 1);
    if (!e.shiftKey) return voegTabIn();
    const tab = tabVoorCursor();
    if (tab) bewerk(() => tab.remove());
  };

  /** Staat de cursor in rechts uitgelijnde tekst, zoals de bedragen onder de factuur? */
  const staatRechts = (el: Element): boolean => {
    for (let x: Element | null = el; x && !isHouderEl(x, body); x = x.parentElement) {
      const s = win.getComputedStyle(x);
      if (s.display !== "inline") return s.textAlign === "right" || s.textAlign === "end";
    }
    return false;
  };

  /**
   * Tab in rechts uitgelijnde tekst: het blok eromheen een tabstop breder, zodat de tekst een
   * tabstop naar rechts gaat.
   *
   * <p>Zo gevonden: Tab vóór {Factuur.TotaalExclBtw} deed niets. Het bedrag staat rechts in zijn vak;
   * een tab ervoor maakte het vak alleen naar links breder en het bedrag bleef staan. Wie daar op
   * Tab drukt wil het bedrag naar rechts — dat kan alleen door het blok (de totalen) breder te maken,
   * en dan gaan alle bedragen mee en blijven ze onder elkaar staan. Shift+Tab maakt hem weer smaller.
   */
  const schuifRechts = (el: Element, richting: 1 | -1) => {
    let blok: HTMLElement | null = null;
    for (let x: Element | null = el; x && !isHouderEl(x, body); x = x.parentElement) {
      if (win.getComputedStyle(x).display !== "inline") blok = x as HTMLElement;
    }
    if (!blok) return;
    const mm = px(d, "1mm") || 3.7795;
    const vak = vakVan(blok);
    const r = blok.getBoundingClientRect();
    const max = vak.rechts - r.left;
    const breed = Math.min(Math.max(r.width + richting * TABSTOP_MM * mm, 20 * mm), max);
    if (Math.abs(breed - r.width) < 0.5 || vak.breed <= 0) {
      toonMelding(richting > 0 ? "Staat al tegen de rechtermarge" : "Kan niet smaller", false);
      return;
    }
    const doel = blok;
    bewerk(() => { doel.style.width = `${((breed / vak.breed) * 100).toFixed(1)}%`; });
  };

  // ── Plaatjes groter of kleiner, zoals in Word ──────────────────────────────────────────────
  // Een nagebouwd logo komt soms te klein uit (het CBF-keurmerk in de voettekst van een shopfactuur).
  // Dat hoeft de AI niet perfect te doen, als je het zelf kunt rechttrekken: sleep aan de greep
  // rechtsonder. De verhouding blijft staan en de breedte gaat in mm in het sjabloon, zodat de pdf
  // hem net zo groot drukt.
  const beeldRand = d.createElement("div");
  beeldRand.className = "ce-beeldrand";
  beeldRand.setAttribute("aria-hidden", "true");
  const beeldGreep = d.createElement("div");
  beeldGreep.className = "ce-beeldgreep";
  beeldGreep.title = "Sleep om het plaatje groter of kleiner te maken";
  lagen.append(beeldRand, beeldGreep);
  let beeld: HTMLImageElement | null = null;
  let beeldSleep: { x0: number; breed0: number; max: number; voor: string } | null = null;

  const toonBeeld = (img: HTMLImageElement | null) => {
    beeld = img;
    if (!img) {
      beeldRand.style.display = "none";
      beeldGreep.style.display = "none";
      return;
    }
    const r = img.getBoundingClientRect();
    beeldRand.style.cssText = `display:block;left:${r.left + scrollX()}px;top:${r.top + scrollY()}px;`
      + `width:${r.width}px;height:${r.height}px`;
    beeldGreep.style.cssText = `display:block;left:${r.right + scrollX()}px;top:${r.bottom + scrollY()}px`;
  };

  const opBeeldBeweging = (e: MouseEvent) => {
    if (opties.alleenLezen) return;
    if (beeldSleep && beeld) {
      e.preventDefault();
      const mmPx = px(d, "1mm") || 3.78;
      const breed = Math.min(Math.max(beeldSleep.breed0 + (e.clientX - beeldSleep.x0), 5 * mmPx), beeldSleep.max);
      beeld.removeAttribute("width");
      beeld.removeAttribute("height");
      beeld.style.width = `${(breed / mmPx).toFixed(1)}mm`;
      beeld.style.height = "auto";
      beeld.style.maxWidth = "100%";
      toonBeeld(beeld);
      const r = beeld.getBoundingClientRect();
      toonGids(r.right, r.top, r.height, breed);
      return;
    }
    const doel = e.target as Element | null;
    if (doel === beeldGreep) return;
    const img = doel && doel.tagName === "IMG" && body.contains(doel) ? (doel as HTMLImageElement) : null;
    if (img) toonBeeld(img);
    else if (beeld && doel !== beeldRand) toonBeeld(null);
  };

  const opBeeldBegin = (e: MouseEvent) => {
    if (e.target !== beeldGreep || !beeld || e.button !== 0 || opties.alleenLezen) return;
    e.preventDefault();
    e.stopPropagation();
    const r = beeld.getBoundingClientRect();
    beeldSleep = { x0: e.clientX, breed0: r.width, max: vakVan(beeld).rechts - r.left, voor: body.innerHTML };
    html.classList.add("ce-beeldsleep");
  };

  const opBeeldEinde = () => {
    if (!beeldSleep) return;
    const voor = beeldSleep.voor;
    beeldSleep = null;
    html.classList.remove("ce-beeldsleep");
    gids.style.display = "none";
    if (voor !== body.innerHTML) {
      naWijziging();
      vorige = voor;
    }
    if (beeld) toonBeeld(beeld);
  };

  // ── Randen verslepen, zoals in Word ────────────────────────────────────────────────────────
  // De rechterrand van een tabel of van de totalen, en de rand tussen twee kolommen. Zo komen de
  // bedragen verder naar rechts: Tab kan dat niet, want ze staan al rechts in hun vak.
  type Greep = { soort: "blok"; el: HTMLElement } | { soort: "kolom"; cel: HTMLTableCellElement };
  const RAND = 4;
  const gids = d.createElement("div");
  gids.className = "ce-gids";
  gids.setAttribute("aria-hidden", "true");
  const gidsTekst = d.createElement("span");
  gids.appendChild(gidsTekst);
  lagen.appendChild(gids);
  let greep: Greep | null = null;
  let sleep: {
    greep: Greep; x0: number; breed0: number; buur0: number; max: number; voor: string;
  } | null = null;

  /** De binnenbreedte van het vak waarin een blok staat, en waar die rechts ophoudt. */
  const vakVan = (blok: HTMLElement) => {
    let ouder = blok.parentElement!;
    while (ouder !== body && win.getComputedStyle(ouder).display === "inline") ouder = ouder.parentElement!;
    const s = win.getComputedStyle(ouder);
    const r = ouder.getBoundingClientRect();
    const links = r.left + (parseFloat(s.paddingLeft) || 0) + (parseFloat(s.borderLeftWidth) || 0);
    const rechts = r.right - (parseFloat(s.paddingRight) || 0) - (parseFloat(s.borderRightWidth) || 0);
    return { links, rechts, breed: rechts - links };
  };

  /** Een blok waarvan de breedte iets betekent: een tabel, of iets smallers dan de pagina. */
  const heeftBreedte = (b: HTMLElement) =>
    b.tagName === "TABLE" || win.getComputedStyle(b).display === "table"
    || b.getBoundingClientRect().right < vakVan(b).rechts - 2;

  const handvat = d.createElement("div");
  handvat.className = "ce-handvat";
  handvat.title = "Sleep om breder of smaller te maken";
  lagen.appendChild(handvat);
  let handvatBlok: HTMLElement | null = null;

  const toonHandvat = (blok: HTMLElement | null) => {
    handvatBlok = blok;
    if (!blok) {
      handvat.style.display = "none";
      return;
    }
    const r = blok.getBoundingClientRect();
    handvat.style.cssText = `display:block;left:${r.right + scrollX()}px;top:${r.top + scrollY()}px;`
      + `height:${Math.max(r.height, 16)}px`;
  };

  /** Het buitenste blok onder de muis; staat de muis net rechts ervan, dan het blok op die hoogte. */
  const blokOnder = (e: MouseEvent): HTMLElement | null => {
    const doel = e.target as Element | null;
    if (!doel || !body.contains(doel) || doel.closest(".db-header, .db-footer")) return null;
    let blok: HTMLElement | null = null;
    if (isHouderEl(doel, body)) {
      blok = (Array.from(doel.children) as HTMLElement[]).find((k) => {
        const r = k.getBoundingClientRect();
        return !isKopOfVoet(k) && e.clientY >= r.top && e.clientY <= r.bottom;
      }) ?? null;
    } else {
      for (let x: Element | null = doel; x && !isHouderEl(x, body); x = x.parentElement) {
        if (win.getComputedStyle(x).display !== "inline") blok = x as HTMLElement;
      }
    }
    return blok && heeftBreedte(blok) ? blok : null;
  };

  const zoekGreep = (e: MouseEvent): Greep | null => {
    const doel = e.target as Element | null;
    if (!doel || !body.contains(doel) || doel.closest(".db-header, .db-footer")) return null;
    const cel = doel.closest("td, th") as HTMLTableCellElement | null;
    if (cel && body.contains(cel)) {
      const r = cel.getBoundingClientRect();
      if (Math.abs(e.clientX - r.right) <= RAND && cel.nextElementSibling) return { soort: "kolom", cel };
      const vorige = cel.previousElementSibling as HTMLTableCellElement | null;
      if (Math.abs(e.clientX - r.left) <= RAND && vorige) return { soort: "kolom", cel: vorige };
    }
    // Het buitenste blok onder de muis; staat de muis net rechts ervan, dan het blok op die hoogte.
    let blok: HTMLElement | null = null;
    if (isHouderEl(doel, body)) {
      blok = (Array.from(doel.children) as HTMLElement[]).find((k) => {
        const r = k.getBoundingClientRect();
        return !isKopOfVoet(k) && e.clientY >= r.top && e.clientY <= r.bottom;
      }) ?? null;
    } else {
      for (let x: Element | null = doel; x && !isHouderEl(x, body); x = x.parentElement) {
        if (win.getComputedStyle(x).display !== "inline") blok = x as HTMLElement;
      }
    }
    if (!blok || !heeftBreedte(blok)) return null;
    const r = blok.getBoundingClientRect();
    return Math.abs(e.clientX - r.right) <= RAND ? { soort: "blok", el: blok } : null;
  };

  const toonGids = (x: number, boven: number, hoog: number, breedPx: number) => {
    const mm = px(d, "100mm") / 100 || 3.7795;
    gids.style.cssText = `display:block;left:${x + scrollX()}px;top:${boven + scrollY()}px;height:${hoog}px`;
    gidsTekst.textContent = `${(breedPx / mm / 10).toFixed(1).replace(".", ",")} cm`;
  };

  const opSleepBeweging = (e: MouseEvent) => {
    if (!sleep) {
      if (opties.alleenLezen) return;
      if (e.target === handvat) return; // boven het handvat zelf: laten staan
      greep = zoekGreep(e);
      html.classList.toggle("ce-sleepbaar", !!greep);
      toonHandvat(blokOnder(e));
      return;
    }
    e.preventDefault();
    const s = sleep;
    const dx = e.clientX - s.x0;
    if (s.greep.soort === "blok") {
      const blok = s.greep.el;
      const vak = vakVan(blok);
      const breed = Math.min(Math.max(s.breed0 + dx, 10 * (px(d, "1mm") || 3.78)), s.max);
      blok.style.width = `${((breed / vak.breed) * 100).toFixed(1)}%`;
      const r = blok.getBoundingClientRect();
      toonGids(r.right, r.top, r.height, breed);
      toonHandvat(blok);
    } else {
      const cel = s.greep.cel;
      const buur = cel.nextElementSibling as HTMLTableCellElement;
      const tabel = cel.closest("table")!;
      const tb = tabel.getBoundingClientRect().width;
      const min = 8 * (px(d, "1mm") || 3.78);
      const breed = Math.min(Math.max(s.breed0 + dx, min), s.breed0 + s.buur0 - min);
      const buurBreed = s.breed0 + s.buur0 - breed;
      // De hele kolom, niet één cel: in een tabel met vaste indeling telt anders alleen de eerste rij.
      const { start } = kolomVan(cel);
      const { start: buurStart } = kolomVan(buur);
      for (const rij of Array.from(tabel.rows).filter((r) => r.closest("table") === tabel)) {
        const a = celOp(rij, start);
        const b = celOp(rij, buurStart);
        if (a && (a.cel.colSpan || 1) === 1) a.cel.style.width = `${((breed / tb) * 100).toFixed(1)}%`;
        if (b && (b.cel.colSpan || 1) === 1) b.cel.style.width = `${((buurBreed / tb) * 100).toFixed(1)}%`;
      }
      const r = cel.getBoundingClientRect();
      toonGids(r.right, tabel.getBoundingClientRect().top, tabel.getBoundingClientRect().height, breed);
    }
  };

  const opSleepBegin = (e: MouseEvent) => {
    if (e.target === handvat && handvatBlok) greep = { soort: "blok", el: handvatBlok };
    if (!greep || e.button !== 0 || opties.alleenLezen) return;
    e.preventDefault();
    e.stopPropagation();
    sluitMenu();
    verberg();
    if (greep.soort === "blok") {
      const r = greep.el.getBoundingClientRect();
      sleep = { greep, x0: e.clientX, breed0: r.width, buur0: 0, max: vakVan(greep.el).rechts - r.left, voor: body.innerHTML };
    } else {
      const buur = greep.cel.nextElementSibling as HTMLElement;
      sleep = {
        greep, x0: e.clientX, breed0: greep.cel.getBoundingClientRect().width,
        buur0: buur.getBoundingClientRect().width, max: 0, voor: body.innerHTML,
      };
    }
  };

  const opSleepEinde = () => {
    if (!sleep) return;
    const voor = sleep.voor;
    sleep = null;
    gids.style.display = "none";
    html.classList.remove("ce-sleepbaar");
    if (voor !== body.innerHTML) {
      naWijziging();
      vorige = voor;
    }
  };

  // Chrome zet de cursor na een klik soms in een veld; daar hoort hij niet.
  const opSelectie = () => {
    if (!sleep) haalUitVeld(d);
  };

  const opToets = (e: KeyboardEvent) => {
    if (e.key === "Escape") sluitMenu();
    if (e.key === "Tab" && !e.ctrlKey && !e.altKey && !e.metaKey && !opties.alleenLezen) opTab(e);
    // Ctrl+Z na een rij, kolom of herhaalblok: die kent de browser niet, dus die zetten we zelf terug.
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z" && vorige !== null) {
      e.preventDefault();
      zetTerug();
    }
  };
  const opInvoer = () => {
    vorige = null;
    melding.style.display = "none";
    plan();
  };
  const opKlik = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node)) sluitMenu();
  };

  body.addEventListener("mousemove", opBeweging);
  body.addEventListener("mouseleave", (e) => {
    // Naar de knoppen toe bewegen is geen "weg": die liggen buiten de body.
    const naar = e.relatedTarget as Node | null;
    if (naar && lagen.contains(naar)) return;
    verberg();
  });
  knoppen.addEventListener("mouseleave", (e) => {
    const naar = e.relatedTarget as Node | null;
    if (naar && (body.contains(naar) || knoppen.contains(naar))) return;
    verberg();
  });
  body.addEventListener("contextmenu", opMenu);
  d.addEventListener("keydown", opToets);
  d.addEventListener("mousedown", opKlik);
  d.addEventListener("mousemove", opSleepBeweging);
  d.addEventListener("mousemove", opBeeldBeweging);
  d.addEventListener("mousedown", opBeeldBegin, true);
  d.addEventListener("mouseup", opBeeldEinde);
  d.addEventListener("mousedown", opSleepBegin, true);
  d.addEventListener("mouseup", opSleepEinde);
  d.addEventListener("selectionchange", opSelectie);
  d.defaultView?.addEventListener("scroll", sluitMenu);
  body.addEventListener("input", opInvoer);
  d.defaultView?.addEventListener("resize", plan);
  // Plaatjes (het logo, een handtekening) laden later dan de tekst en veranderen de hoogte.
  for (const img of Array.from(body.querySelectorAll("img"))) img.addEventListener("load", plan);

  herbereken();
  klaar = true;

  return {
    herbereken,
    bewerk,
    weg() {
      body.removeEventListener("mousemove", opBeweging);
      body.removeEventListener("contextmenu", opMenu);
      body.removeEventListener("input", opInvoer);
      d.removeEventListener("keydown", opToets);
      d.removeEventListener("mousedown", opKlik);
      d.removeEventListener("mousemove", opSleepBeweging);
      d.removeEventListener("mousemove", opBeeldBeweging);
      d.removeEventListener("mousedown", opBeeldBegin, true);
      d.removeEventListener("mouseup", opBeeldEinde);
      html.classList.remove("ce-beeldsleep");
      d.removeEventListener("mousedown", opSleepBegin, true);
      d.removeEventListener("mouseup", opSleepEinde);
      d.removeEventListener("selectionchange", opSelectie);
      html.classList.remove("ce-sleepbaar");
      d.defaultView?.removeEventListener("scroll", sluitMenu);
      d.defaultView?.removeEventListener("resize", plan);
      if (gepland) d.defaultView?.clearTimeout(gepland);
      vellen.remove();
      lagen.remove();
      stijl.remove();
      verschuif.remove();
      html.style.removeProperty("--ce-body-hoogte");
      html.style.removeProperty("--ce-voet-top");
    },
  };
}
